import React, { useState } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Divider,
  IconButton,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useAuth } from "../context/AuthContext";

interface LoginProps {
  onSwitchToRegister?: () => void;
  onBackToHome?: () => void;
}

export const Login: React.FC<LoginProps> = ({
  onSwitchToRegister,
  onBackToHome,
}) => {
  const { login, enterDemoMode } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login(email, password);
    } catch (error) {
      setError("Invalid email or password");
    }
  };

  const handleDemoMode = () => {
    enterDemoMode();
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "80vh",
      }}
    >
      <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: "100%" }}>
        {onBackToHome && (
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <IconButton
              onClick={onBackToHome}
              sx={{
                color: "#6B7B8C",
                "&:hover": {
                  color: "#1A1A1A",
                  backgroundColor: "#F4EDE4",
                },
              }}
            >
              <ArrowBack />
            </IconButton>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              Back to Home
            </Typography>
          </Box>
        )}
        <Typography variant="h5" gutterBottom align="center">
          Login to Your Wardrobe
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
          />
          <Button type="submit" variant="contained" fullWidth sx={{ mt: 3 }}>
            Login
          </Button>
        </form>

        <Divider sx={{ my: 3 }}>
          <Typography variant="body2" color="text.secondary">
            OR
          </Typography>
        </Divider>

        {onSwitchToRegister && (
          <Button
            variant="outlined"
            fullWidth
            onClick={onSwitchToRegister}
            sx={{
              mt: 2,
              py: 1.5,
              borderColor: "#6B7B8C",
              color: "#1A1A1A",
              fontWeight: 500,
              fontSize: "1rem",
              borderRadius: 2,
              borderWidth: 2,
              "&:hover": {
                borderColor: "#1A1A1A",
                backgroundColor: "#F4EDE4",
                borderWidth: 2,
              },
            }}
          >
            Create New Account
          </Button>
        )}

        <Button
          variant="outlined"
          fullWidth
          onClick={handleDemoMode}
          sx={{
            mt: 2,
            py: 1.5,
            borderColor: "#C49A88",
            color: "#1A1A1A",
            fontWeight: 500,
            fontSize: "1rem",
            borderRadius: 2,
            borderWidth: 2,
            "&:hover": {
              borderColor: "#B08570",
              backgroundColor: "#F4EDE4",
              borderWidth: 2,
            },
          }}
        >
          ✨ Try Demo Mode
        </Button>

        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ mt: 2, mb: 3 }}
        >
          Experience the app with sample wardrobe items
        </Typography>
      </Paper>
    </Box>
  );
};
